import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import * as I from 'immutable';

type DisabilityProps = {
  plan: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  monthlySalary: StringUnion;
  benefitPeriod: StringUnion;
  monthlyBenefit: StringUnion;
  type: StringUnion;
  accident: StringUnion;
  sickness: StringUnion;
  occClass: StringUnion;
  section125: boolean;
  jetIssue: boolean;
  accidents: I.List<string>;
  sicknesses: I.List<string>;
  periods: I.List<number>;
  occClasses: I.List<string>;
  product: StringUnion;
  planList: I.List<string>;
};

export class InvalidNewYorkDisability {
  public readonly key = 'InvalidNewYorkDisability';

  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly monthlySalary: StringUnion,
    public readonly benefitPeriod: StringUnion,
    public readonly monthlyBenefit: StringUnion,
    public readonly type: StringUnion,
    public readonly accident: StringUnion,
    public readonly sickness: StringUnion,
    public readonly occClass: StringUnion,
    public readonly section125: boolean,
    public readonly jetIssue: boolean,
    public readonly accidents: I.List<string>,
    public readonly sicknesses: I.List<string>,
    public readonly periods: I.List<number>,
    public readonly occClasses: I.List<string>,
    public readonly product: StringUnion,
    public readonly planList: I.List<string>
  ) {}
}

export class ValidNewYorkDisability {
  public static createApp({
    plan,
    premium,
    calculatedPremium,
    cwa,
    monthlySalary,
    benefitPeriod,
    monthlyBenefit,
    type,
    accident,
    sickness,
    occClass,
    section125,
    jetIssue,
    accidents,
    sicknesses,
    periods,
    occClasses,
    product,
    planList,
  }: DisabilityProps) {
    return this.create(
      plan,
      premium,
      calculatedPremium,
      cwa,
      monthlySalary,
      benefitPeriod,
      monthlyBenefit,
      type,
      accident,
      sickness,
      occClass,
      section125,
      jetIssue,
      accidents,
      sicknesses,
      periods,
      occClasses,
      product,
      planList
    );
  }

  public static create(
    plan: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    monthlySalary: StringUnion,
    benefitPeriod: StringUnion,
    monthlyBenefit: StringUnion,
    type: StringUnion,
    accident: StringUnion,
    sickness: StringUnion,
    occClass: StringUnion,
    section125: boolean,
    jetIssue: boolean,
    accidents: I.List<string>,
    sicknesses: I.List<string>,
    periods: I.List<number>,
    occClasses: I.List<string>,
    product: StringUnion,
    planList: I.List<string>
  ) {
    return isNonEmptyString(plan)
      ? new ValidNewYorkDisability(
          plan,
          premium,
          calculatedPremium,
          cwa,
          monthlySalary,
          benefitPeriod,
          monthlyBenefit,
          type,
          accident,
          sickness,
          occClass,
          section125,
          jetIssue,
          accidents,
          sicknesses,
          periods,
          occClasses,
          product,
          planList
        )
      : new InvalidNewYorkDisability(
          plan,
          premium,
          calculatedPremium,
          cwa,
          monthlySalary,
          benefitPeriod,
          monthlyBenefit,
          type,
          accident,
          sickness,
          occClass,
          section125,
          jetIssue,
          accidents,
          sicknesses,
          periods,
          occClasses,
          product,
          planList
        );
  }

  public readonly key = 'ValidNewYorkDisability';
  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly monthlySalary: StringUnion,
    public readonly benefitPeriod: StringUnion,
    public readonly monthlyBenefit: StringUnion,
    public readonly type: StringUnion,
    public readonly accident: StringUnion,
    public readonly sickness: StringUnion,
    public readonly occClass: StringUnion,
    public readonly section125: boolean,
    public readonly jetIssue: boolean,
    public readonly accidents: I.List<string>,
    public readonly sicknesses: I.List<string>,
    public readonly periods: I.List<number>,
    public readonly occClasses: I.List<string>,
    public readonly product: StringUnion,
    public readonly planList: I.List<string>
  ) {}
}

export type NewYorkDisability = InvalidNewYorkDisability | ValidNewYorkDisability;

export type NewYorkDisabilityOptions<TReturn> = ToPartialMatchOptions<NewYorkDisability, 'key', TReturn>;

export const matchNewYorkDisability = <TReturn>(options: NewYorkDisabilityOptions<TReturn>) => (
  app: NewYorkDisability
): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkDisability':
      return options.InvalidNewYorkDisability ? options.InvalidNewYorkDisability(app) : options.default(app);
    case 'ValidNewYorkDisability':
      return options.ValidNewYorkDisability ? options.ValidNewYorkDisability(app) : options.default(app);
  }
};

export const EmptyNewYorkDisability = new InvalidNewYorkDisability(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  I.List<string>(),
  I.List<string>(),
  I.List<number>(),
  I.List<string>(),
  new EmptyString(),
  I.List<string>()
);

export const changePlan = (plan: string) => (app: NewYorkDisability) => {
  if (app.product!.value) {
    const product = app.product.value;
    const accidentList: I.List<string> = I.List<string>(accidents[product][plan]);
    const sicknessList: I.List<string> = I.List<string>(sicknesses[product][plan]);
    const periodList: I.List<number> = I.List<number>(periods[product][plan]);
    const occClassList: I.List<string> = I.List<string>(occClasses[product][plan]);
    return ValidNewYorkDisability.createApp({
      ...app,
      plan: NonEmptyString.create(plan),
      accidents: accidentList,
      accident: accidentList.size === 1 ? NonEmptyString.create(accidentList.get(0)!) : app.accident,
      sicknesses: sicknessList,
      sickness: sicknessList.size === 1 ? NonEmptyString.create(sicknessList.get(0)!) : app.sickness,
      periods: periodList,
      benefitPeriod: periodList.size === 1 ? NonEmptyString.create(periodList.get(0)!.toString()) : app.benefitPeriod,
      occClasses: occClassList,
      occClass: occClassList.size === 1 ? NonEmptyString.create(occClassList.get(0)!) : app.occClass,
    });
  }
  return ValidNewYorkDisability.createApp({...app, product: NonEmptyString.create(plan)});
};

export const changeProduct = (product: string) => (app: NewYorkDisability) => {
  const planList: I.List<string> = I.List<string>(plans[product]);

  app = EmptyNewYorkDisability;
  app = ValidNewYorkDisability.createApp({...app, planList: planList, product: NonEmptyString.create(product)});

  return changePlan(planList.size === 1 ? planList.get(0)! : app.plan.value)(app);
};

export const changePremium = (premium: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeBenefitPeriod = (benefitPeriod: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, benefitPeriod: NonEmptyString.create(benefitPeriod)});
export const changeBenefitMonthly = (monthlyBenefit: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, monthlyBenefit: NonEmptyString.create(monthlyBenefit)});
export const changeMonthlySalary = (monthlySalary: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, monthlySalary: NonEmptyString.create(monthlySalary)});
export const changeType = (type: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, type: NonEmptyString.create(type)});
export const changeAccident = (accident: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, accident: NonEmptyString.create(accident)});
export const changeSickness = (sick: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, sickness: NonEmptyString.create(sick)});
export const changeOccClass = (occ: string) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, occClass: NonEmptyString.create(occ)});
export const changeSection125 = (section125: boolean) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, section125});
export const changeJetIssue = (jetIssue: boolean) => (app: NewYorkDisability) =>
  ValidNewYorkDisability.createApp({...app, jetIssue});
export const accidents = {
  Disability: {NYDI5: ['000', '007', '014', '030', '090', '180']},
  DITWUNY: {NYDYT: ['14'], NYDYH: ['14']},
};
export const sicknesses = {
  Disability: {NYDI5: accidents.Disability.NYDI5.slice(1)},
  DITWUNY: {NYDYT: ['14'], NYDYH: ['14']},
};
export const occClasses = {Disability: {NYDI5: ['P', 'S']}, DITWUNY: {}};
export const periods = {Disability: {NYDI5: [3, 6, 12, 24]}, DITWUNY: {NYDYT: [24], NYDYH: [24]}};
export const plans = {Disability: ['NYDI5'], DITWUNY: ['NYDYH', 'NYDYT']};
export const types = {Disability: ['Simplified', 'Contingent'], DITWUNY: ['Simplified', 'Contingent', 'Guaranteed']};
